import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { Archive, Armchair, Bath, CookingPot, CupSoda, Package, SprayCan } from 'lucide-react'
import { cn } from '@/lib/utils'

const ICONS: Record<string, LucideIcon> = {
    kitchen: CookingPot,
    storage: Archive,
    cleaning: SprayCan,
    furniture: Armchair,
    packaging: CupSoda,
    home: Bath
}

export const categoryIcon = (slug: string | null | undefined): LucideIcon => (slug && ICONS[slug]) || Package

interface ProductMediaProps {
    src: string | null
    alt: string
    categorySlug?: string | null
    className?: string
    imageClassName?: string
    sizes?: string
    priority?: boolean
    iconClassName?: string
}

/** Product photo, or a clean branded tile when no photo has been uploaded yet. */
export const ProductMedia: FC<ProductMediaProps> = ({ src, alt, categorySlug, className, imageClassName, priority, iconClassName }): ReactNode => {
    const Icon = categoryIcon(categorySlug)

    return (
        <div className={cn('relative overflow-hidden bg-gradient-to-br from-navy-50 via-white to-cyan-50', className)}>
            {src
                ? (
                    <img
                        src={src}
                        alt={alt}
                        loading={priority ? 'eager' : 'lazy'}
                        decoding='async'
                        className={cn('h-full w-full object-cover', imageClassName)}
                    />
                )
                : (
                    <div className='absolute inset-0 flex items-center justify-center' role='img' aria-label={alt}>
                        <div className='absolute inset-0 bg-dots opacity-60' aria-hidden='true' />
                        <div className='absolute size-[62%] rounded-full bg-gradient-to-br from-cyan-200/50 to-navy-100/40 blur-2xl' aria-hidden='true' />
                        <div className='relative flex aspect-square w-[38%] items-center justify-center rounded-[28%] bg-white/80 shadow-soft ring-1 ring-navy-100 backdrop-blur'>
                            <Icon className={cn('size-[46%] text-navy-700', iconClassName)} strokeWidth={1.4} aria-hidden='true' />
                        </div>
                    </div>
                )}
        </div>
    )
}
